import React, { useReducer, ChangeEvent } from "react";
import { styled } from "@iventis/styles";

import { searchMapboxApi } from "./map-search-api-functions";
import { initialMapSearchReducerState, mapSearchReducer } from "./map-search-state";
import { useSubjectContext } from "../../state/subjects-context";

const BUFFER_DISTANCE = 0.001; // Buffer distance to create a bounding box around selected coordinate locations

export function MapSearch() {
    const { searchLocationSelected$ } = useSubjectContext();
    const [state, dispatch] = useReducer(mapSearchReducer, initialMapSearchReducerState);

    const handleMapSearchChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const searchString = event.target.value;
        dispatch({
            type: "update-search-string",
            payload: { request: setupApiCall(searchString), searchString },
        });
    };

    const setupApiCall = (searchString: string) =>
        setTimeout(async () => {
            const results = await searchMapboxApi(searchString);
            dispatch({ type: "update-results", payload: results.features });
        }, 1000);

    const handleResultClicked = (locationBbox: [number, number, number, number] | undefined, coordinates: { longitude: number; latitude: number }, name: string) => {
        let bbox = locationBbox;
        if (!locationBbox) {
            const buffer = BUFFER_DISTANCE;
            bbox = [coordinates.longitude - buffer, coordinates.latitude - buffer, coordinates.longitude + buffer, coordinates.latitude + buffer];
        }
        searchLocationSelected$.next(bbox);
        dispatch({ type: "result-clicked", payload: name });
    };

    return (
        <MapSearchContainer>
            <input type="text" value={state.searchString} onChange={handleMapSearchChange} placeholder="Search for location" />
            <div className="search-results-list">
                {state.results.map((result) => (
                    <button
                        type="button"
                        className="map-search-result"
                        key={result.properties.mapbox_id}
                        onClick={() => handleResultClicked(result.properties.bbox, result.properties.coordinates, result.properties.full_address)}
                    >
                        {result.properties.feature_type === "poi" ? (
                            <span>{`${result.properties.name} - ${result.properties.full_address}`}</span>
                        ) : (
                            result.properties.full_address ?? result.properties.place_formatted
                        )}
                    </button>
                ))}
                {state.touched && state.results.length === 0 && (
                    <button type="button" className="map-search-result">
                        No results
                    </button>
                )}
            </div>
        </MapSearchContainer>
    );
}

const MapSearchContainer = styled.div`
    width: 100%;
    height: 50px;
    box-sizing: border-box;
    border: 1px solid #000;
    border-radius: 4px;

    input {
        height: 100%;
        width: 100%;
        border: 0px;
        outline: none;
        padding: 5px;
        border-radius: 4px;
        box-sizing: border-box;
    }

    button {
        border-radius: 0px;
        height: 50px;
    }

    .map-search-result {
        background-color: white;
        z-index: 10;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: left;
        border: none;
        width: 100%;
        border-bottom: 1px solid #cccccc;
        position: relative;
    }

    .map-search-result:hover {
        background-color: #ebe4e4;
    }

    .map-search-result:last-child {
        border: 0px;
        border-radius: 0px 0px 4px 4px;
    }

    .map-search-result:first-child {
        border-radius: 4px 4px 0px 0px;
    }

    .search-results-list {
        margin: 1px 5px;
        display: flex;
        flex-direction: column;
    }
`;
